export enum SCREEN_MODES {
  VIEWING_MODE = 'VIEWING_MODE',
  EDITING_MODE = 'EDITING_MODE',
  SELECTING_MODE = 'SELECTING_MODE',
}

export type ScreenMode =
  | SCREEN_MODES.VIEWING_MODE
  | SCREEN_MODES.EDITING_MODE
  | SCREEN_MODES.SELECTING_MODE;

export enum TABS {
  RATES_FIELD = 'ratesFieldMapping',
  RATES_FIXING = 'ratesFixingProxyMapping',
  LEGAL_ENTITY = 'legalEntityMapping',
}

export const TABS_PATHS = {
  [TABS.RATES_FIELD]: 'rates-field',
  [TABS.RATES_FIXING]: 'rates-fixing',
  [TABS.LEGAL_ENTITY]: 'legal-entity',
};

export type Tab = TABS.RATES_FIELD | TABS.RATES_FIXING | TABS.LEGAL_ENTITY;

export enum ACTION_TYPE {
  INSERT = 'INSERT',
  UPDATE = 'UPDATE',
  DELETE = 'DELETE',
}

export type ActionType =
  | ACTION_TYPE.INSERT
  | ACTION_TYPE.UPDATE
  | ACTION_TYPE.DELETE;

export enum FIELD_NAME {
  DAY_COUNT = 'DAY_COUNT',
  PAY_FREQUENCY = 'PAY_FREQUENCY',
  REFERENCE_INDEX = 'REFERENCE_INDEX',
}

export enum COLUMN_ID {
  ACTION = 'action',
  GROUP_ID = 'groupId',
  FIELD_NAME = 'fieldName',
  INPUT_VALUE = 'inputValue',
  MAPPED_VALUE = 'mappedValue',
  INPUT_REFERENCE_INDEX = 'inputReferenceIndex',
  MAPPED_REFERENCE_INDEX = 'mappedReferenceIndex',
  MAPPED_PAY_FREQUENCY = 'mappedPayFrequency',
  MAPPED_DAY_COUNT = 'mappedDayCount',
  LEGAL_ENTITY = 'legalEntity',
  MAPPED_GROUP_ID = 'mappedGroupId',
  VALID_FROM = 'validFrom',
  VALID_TO = 'validTo',
  LAST_UPDATED_BY = 'lastUpdatedBy',
  LAST_UPDATE_TIMESTAMP = 'lastUpdateTimestamp',
}

export const FIXING_PROXY_FIELD_NAME_TO_COLUMN_ID = {
  [FIELD_NAME.DAY_COUNT]: COLUMN_ID.MAPPED_DAY_COUNT,
  [FIELD_NAME.PAY_FREQUENCY]: COLUMN_ID.MAPPED_PAY_FREQUENCY,
  [FIELD_NAME.REFERENCE_INDEX]: COLUMN_ID.MAPPED_REFERENCE_INDEX,
};

export const NON_EMPTY_VALIDATION_COLUMN_IDS = {
  [TABS.RATES_FIELD]: [
    COLUMN_ID.FIELD_NAME,
    COLUMN_ID.INPUT_VALUE,
    COLUMN_ID.MAPPED_VALUE,
    COLUMN_ID.VALID_FROM,
    COLUMN_ID.VALID_TO,
  ],
  [TABS.RATES_FIXING]: [
    COLUMN_ID.INPUT_REFERENCE_INDEX,
    COLUMN_ID.MAPPED_REFERENCE_INDEX,
    COLUMN_ID.VALID_FROM,
    COLUMN_ID.VALID_TO,
  ],
  [TABS.LEGAL_ENTITY]: [
    COLUMN_ID.LEGAL_ENTITY,
    COLUMN_ID.MAPPED_GROUP_ID,
    COLUMN_ID.VALID_FROM,
    COLUMN_ID.VALID_TO,
  ],
};

export const VALIDATION_FIELD_DISPLAY_NAMES: Record<string, string> = {
  [COLUMN_ID.FIELD_NAME]: 'Field Name',
  [COLUMN_ID.INPUT_VALUE]: 'Input Value',
  [COLUMN_ID.MAPPED_VALUE]: 'Mapped Value',
  [COLUMN_ID.INPUT_REFERENCE_INDEX]: 'Input Reference Index',
  [COLUMN_ID.MAPPED_REFERENCE_INDEX]: 'Mapped Reference Index',
  [COLUMN_ID.VALID_FROM]: 'Valid From',
  [COLUMN_ID.VALID_TO]: 'Valid To',
  [COLUMN_ID.GROUP_ID]: 'Group ID',
  [COLUMN_ID.LEGAL_ENTITY]: 'Legal Entity',
  [COLUMN_ID.MAPPED_GROUP_ID]: 'Mapped Group ID',
};

export const DUPLICATE_VALIDATION_COLUMN_IDS = {
  [TABS.RATES_FIELD]: [
    COLUMN_ID.GROUP_ID,
    COLUMN_ID.FIELD_NAME,
    COLUMN_ID.INPUT_VALUE,
  ],
  [TABS.RATES_FIXING]: [COLUMN_ID.GROUP_ID, COLUMN_ID.INPUT_REFERENCE_INDEX],
  [TABS.LEGAL_ENTITY]: [COLUMN_ID.LEGAL_ENTITY],
};

export const MAPPED_VALUES_COLUMN_IDS = [
  COLUMN_ID.MAPPED_VALUE,
  COLUMN_ID.MAPPED_REFERENCE_INDEX,
];

export const SYSTEM_GENERATED_FIELD_NAMES = [
  COLUMN_ID.LAST_UPDATED_BY,
  COLUMN_ID.LAST_UPDATE_TIMESTAMP,
];

export const PRIMARY_KEY_FIELD_NAMES = [
  COLUMN_ID.GROUP_ID,
  COLUMN_ID.FIELD_NAME,
  COLUMN_ID.INPUT_VALUE,
  COLUMN_ID.INPUT_REFERENCE_INDEX,
  COLUMN_ID.LEGAL_ENTITY,
];

export const COLUMN_IDS_EDIT_NOT_ALLOWED = [
  ...PRIMARY_KEY_FIELD_NAMES,
  ...SYSTEM_GENERATED_FIELD_NAMES,
];

export const AUDIT_CSV_TIMESTAMP_COLUMNS = [
  COLUMN_ID.VALID_FROM,
  COLUMN_ID.VALID_TO,
  COLUMN_ID.LAST_UPDATE_TIMESTAMP,
] as const;

/** Column ids for Mapping Admin CSV export timestamp formatting */
export const ADMIN_CSV_TIMESTAMP_COLUMNS = [
  COLUMN_ID.VALID_FROM,
  COLUMN_ID.VALID_TO,
  COLUMN_ID.LAST_UPDATE_TIMESTAMP,
] as const;

export const COLUMN_IDS_FOR_FILE_UPLOAD = {
  [TABS.RATES_FIELD]: [
    COLUMN_ID.GROUP_ID,
    COLUMN_ID.FIELD_NAME,
    COLUMN_ID.INPUT_VALUE,
    COLUMN_ID.MAPPED_VALUE,
    COLUMN_ID.VALID_FROM,
    COLUMN_ID.VALID_TO,
  ],
  [TABS.RATES_FIXING]: [
    COLUMN_ID.GROUP_ID,
    COLUMN_ID.INPUT_REFERENCE_INDEX,
    COLUMN_ID.MAPPED_DAY_COUNT,
    COLUMN_ID.MAPPED_REFERENCE_INDEX,
    COLUMN_ID.MAPPED_PAY_FREQUENCY,
    COLUMN_ID.VALID_FROM,
    COLUMN_ID.VALID_TO,
  ],
  [TABS.LEGAL_ENTITY]: [
    COLUMN_ID.LEGAL_ENTITY,
    COLUMN_ID.MAPPED_GROUP_ID,
    COLUMN_ID.VALID_FROM,
    COLUMN_ID.VALID_TO,
  ],
};
